import { DesktopDatePicker, DesktopDateTimePicker } from "@mui/x-date-pickers";
import { toDate } from "@repo/utils/date";

import {
  calendarDateToPickerDate,
  fromPickerDate,
  pickerDateToCalendarDate,
  toPickerDate,
} from "@/utils/picker-date";

import { useFieldContext } from "./form.context";

type Props = {
  label: string;
  disablePast?: boolean;
  greaterThan?: string;
};

const getErrorMessage = (errors: any[]) => {
  return errors
    .map((error) => {
      if (typeof error === "string") return error;
      if (error?.message) return error.message;
      if (error?._errors?.length) return error._errors[0];
      return String(error);
    })
    .join(", ");
};

/** Instant field, stored as a UTC ISO string and picked in Paris time. */
export function DateTimeField({ label, disablePast = true, greaterThan }: Props) {
  const field = useFieldContext<string | Date | undefined>();
  const errors = field.state.meta.errors;
  const hasError = field.state.meta.isTouched && errors.length > 0;
  const dateValue = toPickerDate(field.state.value);

  // Calculate min datetime
  let minDateTime: Date | undefined;
  if (disablePast) {
    minDateTime = new Date();
  }
  const greaterThanDate = toDate(greaterThan);
  if (greaterThanDate) {
    minDateTime = greaterThanDate;
  }
  return (
    <DesktopDateTimePicker
      label={label}
      value={dateValue}
      onChange={(date) => {
        const instant = fromPickerDate(date);
        field.handleChange(instant ? instant.toISOString() : "");
      }}
      disablePast={disablePast}
      minDateTime={toPickerDate(minDateTime) ?? undefined}
      ampm={false}
      views={["month", "day", "year", "hours", "minutes"]}
      sx={{ width: "100%" }}
      slotProps={{
        textField: {
          name: field.name,
          variant: "standard",
          fullWidth: true,
          error: hasError,
          helperText: hasError ? getErrorMessage(errors) : "",
          onBlur: field.handleBlur,
        },
      }}
    />
  );
}

/** Calendar date field (birth date, licence date), stored as `YYYY-MM-DD`. */
export function DateField({ label, disablePast = false }: Omit<Props, "greaterThan">) {
  const field = useFieldContext<string | undefined>();
  const errors = field.state.meta.errors;
  const hasError = field.state.meta.isTouched && errors.length > 0;

  return (
    <DesktopDatePicker
      label={label}
      value={calendarDateToPickerDate(field.state.value)}
      onChange={(date) => field.handleChange(pickerDateToCalendarDate(date) ?? "")}
      disablePast={disablePast}
      views={["year", "month", "day"]}
      sx={{ width: "100%" }}
      slotProps={{
        textField: {
          name: field.name,
          variant: "standard",
          fullWidth: true,
          error: hasError,
          helperText: hasError ? getErrorMessage(errors) : "",
          onBlur: field.handleBlur,
        },
      }}
    />
  );
}
